// src/hooks/useTableData.js
// -----------------------------------------------------------------------------
// Custom hook for managing CRUD operations, loading/error states, search, sort,
// pagination, and modal dialogs for any data table / entity service.
//
// FUTURE API INTEGRATION NOTES:
// This hook delegates saving (POST/PUT), deleting (DELETE), and fetching (GET)
// to the injected `service` parameter. When migrating to a real backend:
//   1. Swap out mock service implementations in src/api/* with real axiosClient calls.
//   2. The hook's states (loading, submitting, deleting, data, errors) will automatically
//      reflect the async state of real network HTTP requests.
// -----------------------------------------------------------------------------
import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

/**
 * @param {Object} service - API service with methods: getAll, create, update, remove
 * @param {Object} options - Configuration options (idKey, initialSort, pageSize, itemName)
 */
export function useTableData(service, options = {}) {
  const {
    idKey = 'id',
    initialSort = { key: null, direction: 'asc' },
    initialPageSize = 10,
    itemName = 'Record',
  } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [serverErrors, setServerErrors] = useState({});

  // Search, Sort, Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState(initialSort);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Modal / Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [selectedRecord, setSelectedRecord] = useState(null);

  // 1. Fetch All Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await service.getAll();
      const responseBody = res?.data !== undefined ? res.data : res;
      let resultData = [];
      if (responseBody) {
        if (Array.isArray(responseBody)) {
          resultData = responseBody;
        } else if (responseBody.success && Array.isArray(responseBody.data)) {
          resultData = responseBody.data;
        } else if (Array.isArray(responseBody.data)) {
          resultData = responseBody.data;
        } else {
          resultData = responseBody;
        }
      }
      setData(Array.isArray(resultData) ? resultData : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || `Failed to fetch ${itemName.toLowerCase()}s.`;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [service, itemName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // 2. Filter, Sort, Paginate Data
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase();
    return data.filter((item) =>
      Object.values(item).some((val) =>
        val !== null && val !== undefined && String(val).toLowerCase().includes(term)
      )
    );
  }, [data, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize) || 1;

  // Handler for column sort toggle
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  // 3. Modal Controls
  const openCreateModal = () => {
    setFormMode('create');
    setSelectedRecord(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const openEditModal = (record) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const closeFormModal = () => {
    setIsFormOpen(false);
    setSelectedRecord(null);
    setServerErrors({});
  };

  const openDeleteModal = (record) => {
    setSelectedRecord(record);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setSelectedRecord(null);
  };

  // 4. Create / Update API Handler
  const handleSave = async (formData) => {
    setSubmitting(true);
    setServerErrors({});
    try {
      if (formMode === 'create') {
        await service.create(formData);
        toast.success(`${itemName} created successfully.`);
      } else {
        const id = selectedRecord[idKey];
        await service.update(id, formData);
        toast.success(`${itemName} updated successfully.`);
      }
      closeFormModal();
      await fetchData();
    } catch (err) {
      const respData = err?.response?.data;
      if (respData?.errors && typeof respData.errors === 'object') {
        // Map backend field errors e.g. { typeName: ["Name already exists"] }
        const mapped = {};
        Object.keys(respData.errors).forEach((key) => {
          const lowerKey = key.charAt(0).toLowerCase() + key.slice(1);
          mapped[lowerKey] = Array.isArray(respData.errors[key])
            ? respData.errors[key].join(' ')
            : String(respData.errors[key]);
        });
        setServerErrors(mapped);
      }
      const errMsg = respData?.message || respData?.title || `Failed to save ${itemName.toLowerCase()}.`;
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // 5. Delete API Handler
  const handleDelete = async () => {
    if (!selectedRecord) return;
    setDeleting(true);
    const id = selectedRecord[idKey];
    try {
      await service.remove(id);
      toast.success(`${itemName} deleted successfully.`);
      // Optimistic filter or refetch
      setData((prev) => prev.filter((item) => item[idKey] !== id));
      closeDeleteModal();
    } catch (err) {
      const msg = err?.response?.data?.message || `Failed to delete ${itemName.toLowerCase()}.`;
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return {
    // Data & state
    data: sortedData,
    paginatedData,
    loading,
    submitting,
    deleting,
    error,
    serverErrors,

    // Search, Sort, Pagination
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords: sortedData.length,

    // Modal state & actions
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,

    // Handlers
    handleSave,
    handleDelete,
    refetch: fetchData,
  };
}

export default useTableData;
